import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { JOB_TYPE_OPTIONS } from '../constants/jobTypes';

const CATEGORY_OPTIONS = [
  { value: 'legal', label: '法律合规' },
  { value: 'finance', label: '财务审计' },
  { value: 'business', label: '商务拓展' },
  { value: 'engineering', label: '工程技术' },
  { value: 'hr', label: '人力资源' },
  { value: 'supply-chain', label: '供应链' },
  { value: 'other', label: '其他' },
];

function MyJobs() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 当前编辑的岗位
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!user) return;
    api.get('/jobs/user/my')
      .then(res => setJobs(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除这个岗位吗？')) return;
    try {
      await api.delete(`/jobs/${id}`);
      setJobs(jobs.filter(j => j.id !== id));
    } catch (e) {
      alert('删除失败');
    }
  };

  const openEdit = (job) => {
    setErr('');
    setEditing({
      id: job.id,
      title: job.title || '',
      company: job.company || '',
      location: job.location || '',
      jobType: job.jobType || 'overseas-market',
      category: job.category || 'other',
      salary: job.salary || '',
      description: job.description || '',
      requirements: job.requirements || '',
      contactInfo: job.contactInfo || '',
      publishDate: (job.publishDate || '').substring(0, 10),
    });
  };

  const handleEditChange = (e) => {
    setEditing({ ...editing, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    setErr('');
    try {
      const { id, ...payload } = editing;
      await api.put(`/jobs/${id}`, payload);
      // 本地列表同步更新
      setJobs(jobs.map(j => j.id === id ? { ...j, ...payload } : j));
      setEditing(null);
    } catch (e) {
      setErr(e.response?.data?.error || '保存失败');
    }
    setSaving(false);
  };

  if (!user) {
    return (
      <div className="container" style={{ paddingTop: 60, textAlign: 'center' }}>
        <p>请先 <Link to="/login" style={{ color: 'var(--primary)' }}>登录</Link></p>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: 20, paddingBottom: 60 }}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">我发布的岗位</h1>
          <p className="page-subtitle">管理你发布的涉外岗位信息</p>
        </div>
        <Link to="/post-job" className="btn btn-primary">发布新岗位</Link>
      </div>

      {loading ? (
        <div className="loading">加载中...</div>
      ) : jobs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <p className="empty-state-text">你还没有发布过岗位</p>
          <Link to="/post-job" className="btn btn-primary">发布第一个岗位</Link>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {jobs.map(job => (
            <div key={job.id} className="card" style={{ padding: '16px 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Link to={`/jobs/${job.id}`} style={{ textDecoration: 'none' }}>
                    <div className="job-card-title">{job.title}</div>
                  </Link>
                  <div className="job-card-company">{job.company} · {job.location}</div>
                  <div style={{ marginTop: 8, fontSize: '0.82rem', color: 'var(--text-light)' }}>
                    {job.viewCount || 0} 次查看 · {job.commentCount || 0} 条评论
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(job)}>编辑</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/jobs/${job.id}`);
                    alert('链接已复制');
                  }}>分享</button>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--error)' }} onClick={() => handleDelete(job.id)}>删除</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 编辑弹窗 */}
      {editing && (
        <div
          onClick={() => !saving && setEditing(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 2000, display: 'flex', alignItems: 'flex-start',
            justifyContent: 'center', padding: 20, overflow: 'auto',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="edit-modal-content"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: '1.3rem' }}>编辑岗位</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => !saving && setEditing(null)}>关闭</button>
            </div>

            {err && <div style={{ color: 'var(--error)', marginBottom: 12 }}>{err}</div>}

            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <label style={lblStyle}>岗位名称</label>
                <input className="form-input" name="title" value={editing.title} onChange={handleEditChange} />
              </div>
              <div className="edit-row-pair">
                <div>
                  <label style={lblStyle}>公司</label>
                  <input className="form-input" name="company" value={editing.company} onChange={handleEditChange} />
                </div>
                <div>
                  <label style={lblStyle}>地点</label>
                  <input className="form-input" name="location" value={editing.location} onChange={handleEditChange} />
                </div>
              </div>
              <div className="edit-row-pair">
                <div>
                  <label style={lblStyle}>岗位类型</label>
                  <select className="form-input" name="jobType" value={editing.jobType} onChange={handleEditChange}>
                    {JOB_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lblStyle}>类别</label>
                  <select className="form-input" name="category" value={editing.category} onChange={handleEditChange}>
                    {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="edit-row-pair">
                <div>
                  <label style={lblStyle}>薪资</label>
                  <input className="form-input" name="salary" value={editing.salary} onChange={handleEditChange} placeholder="选填" />
                </div>
                <div>
                  <label style={lblStyle}>发布日期</label>
                  <input className="form-input" name="publishDate" type="date" value={editing.publishDate} onChange={handleEditChange} />
                </div>
              </div>
              <div>
                <label style={lblStyle}>岗位描述</label>
                <textarea className="form-input" name="description" rows={5} value={editing.description} onChange={handleEditChange} />
              </div>
              <div>
                <label style={lblStyle}>任职要求</label>
                <textarea className="form-input" name="requirements" rows={4} value={editing.requirements} onChange={handleEditChange} />
              </div>
              <div>
                <label style={lblStyle}>联系方式</label>
                <input className="form-input" name="contactInfo" value={editing.contactInfo} onChange={handleEditChange} />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button className="btn btn-secondary" onClick={() => setEditing(null)} disabled={saving}>取消</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? '保存中...' : '保存修改'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const lblStyle = { display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 6 };

export default MyJobs;
